import * as XLSX from 'xlsx-js-style';
import { attributionAPI } from '../../services/api';

const PRIMARY = '072040';
const LIGHT_GRAY = 'F4F6F8';
const TOTAL_GRAY = 'E8EAED';

const headerStyle = {
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  fill: { fgColor: { rgb: PRIMARY } },
  alignment: { horizontal: 'left', vertical: 'center' },
};

const titleStyle = { font: { bold: true, sz: 14, color: { rgb: PRIMARY } } };
const labelStyle = { font: { bold: true } };
const pctFmt = '0.00"%"';
const rightAlign = { alignment: { horizontal: 'right' } };

// Numeric cell. If v isn't a finite number, returns an empty-string cell so
// xlsx-js-style doesn't emit a numeric XML element with no value.
const numCell = (v, extraStyle = null) => {
  const baseStyle = { ...rightAlign, numFmt: pctFmt, ...(extraStyle || {}) };
  if (typeof v === 'number' && isFinite(v)) {
    return { v, t: 'n', s: baseStyle };
  }
  return { v: '', t: 's', s: { ...rightAlign, ...(extraStyle || {}) } };
};

const textCell = (v, extraStyle = null) => ({
  v: v == null ? '' : String(v),
  t: 's',
  s: extraStyle || {},
});

const buildHeaderSheet = ({ portfolioCode, mode, reportInfo, rangeStart, rangeEnd, periodEnd }) => {
  const rows = [];
  rows.push([textCell('Portfolio Attribution Export', titleStyle), textCell('')]);
  rows.push([textCell(''), textCell('')]);
  const kv = (label, value) => [textCell(label, labelStyle), textCell(value ?? '—')];
  rows.push(kv('Portfolio code', portfolioCode));
  rows.push(kv('Portfolio name', reportInfo?.portfolio_name));
  rows.push(kv('Benchmark', reportInfo?.benchmark_name));
  rows.push(kv('Currency', reportInfo?.currency));
  rows.push(kv('Attribution method', reportInfo?.attribution_method));
  rows.push(kv('Mode', mode));
  if (mode === 'RANGE') {
    rows.push(kv('Window start', rangeStart));
    rows.push(kv('Window end', rangeEnd));
    if (reportInfo?.cadence) rows.push(kv('Aggregation cadence', reportInfo.cadence));
    if (reportInfo?.n_periods) rows.push(kv('Periods covered', String(reportInfo.n_periods)));
  } else if (periodEnd) {
    rows.push(kv('Period end', periodEnd));
  }
  rows.push(kv('Generated', new Date().toISOString().slice(0, 19).replace('T', ' ')));
  if (mode === 'RANGE') {
    rows.push([textCell(''), textCell('')]);
    rows.push([
      textCell(
        'Note: RANGE results use Cariño geometric linking. Tight match to Refinitiv quarterly aggregates for cadence-aligned windows; small residual differences possible.',
        { alignment: { wrapText: true, vertical: 'top' } },
      ),
      textCell(''),
    ]);
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 24 }, { wch: 60 }];
  return ws;
};

const buildPortfolioTotalSheet = (total) => {
  if (!total) return XLSX.utils.aoa_to_sheet([[textCell('No data', labelStyle)]]);
  const aoa = [
    [textCell('Metric', headerStyle), textCell('Value', headerStyle)],
    [textCell('Portfolio Weight (%)', labelStyle), numCell((total.avg_portfolio_weight ?? 0) * 100)],
    [textCell('Benchmark Weight (%)', labelStyle), numCell((total.avg_benchmark_weight ?? 0) * 100)],
    [textCell('Portfolio Return (%)', labelStyle), numCell(total.portfolio_return)],
    [textCell('Benchmark Return (%)', labelStyle), numCell(total.benchmark_return)],
    [textCell('Excess Return (%)', labelStyle), numCell(total.return_difference)],
    [textCell('Allocation Effect (%)', labelStyle), numCell(total.allocation_effect)],
    [textCell('Selection Effect (%)', labelStyle), numCell(total.selection_effect)],
    [textCell('Total Effect (%)', labelStyle), numCell(total.total_effect)],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 28 }, { wch: 14 }];
  return ws;
};

const buildSACSheet = (sacRows, fundsBySac) => {
  if (!sacRows?.length) return XLSX.utils.aoa_to_sheet([[textCell('No data', labelStyle)]]);
  const headers = ['Holding', 'P Wt (%)', 'B Wt (%)', 'Diff Wt (%)',
    'P Ret (%)', 'B Ret (%)', 'Diff Ret (%)',
    'Allocation (%)', 'Selection (%)', 'Total (%)'];
  const aoa = [headers.map(h => textCell(h, headerStyle))];
  const sacStyle = { font: { bold: true }, fill: { fgColor: { rgb: LIGHT_GRAY } } };
  for (const sac of sacRows) {
    const wd = (sac.avg_portfolio_weight ?? 0) - (sac.avg_benchmark_weight ?? 0);
    aoa.push([
      textCell(sac.sub_asset_class, sacStyle),
      numCell((sac.avg_portfolio_weight ?? 0) * 100, sacStyle),
      numCell((sac.avg_benchmark_weight ?? 0) * 100, sacStyle),
      numCell(wd * 100, sacStyle),
      numCell(sac.portfolio_return, sacStyle),
      numCell(sac.benchmark_return, sacStyle),
      numCell(sac.return_difference, sacStyle),
      numCell(sac.allocation_effect, sacStyle),
      numCell(sac.selection_effect, sacStyle),
      numCell(sac.total_effect, sacStyle),
    ]);
    for (const fund of fundsBySac[sac.sub_asset_class] || []) {
      aoa.push([
        textCell('    ' + (fund.issue_name || '')),
        numCell((fund.avg_portfolio_weight ?? 0) * 100),
        numCell((fund.avg_benchmark_weight ?? 0) * 100),
        numCell((fund.avg_weight_difference ?? 0) * 100),
        numCell(fund.portfolio_return),
        numCell(fund.benchmark_return),
        numCell(fund.return_difference),
        textCell('—', rightAlign),
        numCell(fund.selection_effect),
        numCell(fund.total_effect),
      ]);
    }
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 36 }, ...Array(9).fill({ wch: 12 })];
  return ws;
};

const buildKeyStatsSheet = (stats) => {
  if (!stats?.length) return XLSX.utils.aoa_to_sheet([[textCell('No data', labelStyle)]]);
  const aoa = [
    [textCell('Statistic', headerStyle), textCell('Portfolio', headerStyle),
     textCell('Benchmark', headerStyle), textCell('Difference', headerStyle)],
  ];
  for (const s of stats) {
    aoa.push([
      textCell(s.statistic_name, labelStyle),
      numCell(s.portfolio),
      numCell(s.benchmark),
      numCell(s.difference),
    ]);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  return ws;
};

const buildContributorsSheet = (contributions) => {
  if (!contributions?.length) return XLSX.utils.aoa_to_sheet([[textCell('No data', labelStyle)]]);
  const aoa = [
    [textCell('Scope', headerStyle), textCell('Holding', headerStyle),
     textCell('Active Weight (%)', headerStyle), textCell('Contribution (%)', headerStyle)],
  ];
  const sorted = [...contributions].sort((a, b) => {
    if (a.scope !== b.scope) return a.scope.localeCompare(b.scope);
    return (b.contribution_to_portfolio_return ?? 0) - (a.contribution_to_portfolio_return ?? 0);
  });
  for (const c of sorted) {
    aoa.push([
      textCell(c.scope, labelStyle),
      textCell(c.issue_name),
      numCell((c.weight_difference ?? 0) * 100),
      numCell(c.contribution_to_portfolio_return),
    ]);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 10 }, { wch: 40 }, { wch: 18 }, { wch: 18 }];
  return ws;
};

const sanitiseFilename = (s) => String(s).replace(/[^a-zA-Z0-9_\-.]/g, '_');

export const exportAttributionWorkbook = async ({
  portfolioCode, reportType, periodEnd, rangeStart, rangeEnd,
}) => {
  if (!portfolioCode) throw new Error('No portfolio selected');

  const isRange = reportType === 'RANGE';
  const params = isRange
    ? { start_date: rangeStart, end_date: rangeEnd }
    : (() => {
        const p = { report_type: reportType };
        if (reportType !== 'SI' && periodEnd) p.period_end = periodEnd;
        return p;
      })();

  const calls = isRange
    ? [
        attributionAPI.getRangeReportInfo(portfolioCode, params),
        attributionAPI.getRangeKeyStats(portfolioCode, params),
        attributionAPI.getRangeBrinsonPortfolioTotal(portfolioCode, params),
        attributionAPI.getRangeBrinsonSAC(portfolioCode, params),
        attributionAPI.getRangeBrinsonFunds(portfolioCode, params),
        attributionAPI.getRangeContributions(portfolioCode, params),
      ]
    : [
        attributionAPI.getReport(portfolioCode, params),
        attributionAPI.getKeyStats(portfolioCode, params),
        attributionAPI.getBrinsonPortfolioTotal(portfolioCode, params),
        attributionAPI.getBrinsonSAC(portfolioCode, params),
        attributionAPI.getBrinsonFunds(portfolioCode, params),
        attributionAPI.getContributions(portfolioCode, params),
      ];

  const [reportInfo, keyStats, portfolioTotal, sacRows, fundRows, contributions] = await Promise.all(calls);

  const fundsBySac = {};
  (fundRows || []).forEach(f => {
    if (!fundsBySac[f.sub_asset_class]) fundsBySac[f.sub_asset_class] = [];
    fundsBySac[f.sub_asset_class].push(f);
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildHeaderSheet({
    portfolioCode, mode: reportType, reportInfo, rangeStart, rangeEnd, periodEnd,
  }), 'Cover');
  XLSX.utils.book_append_sheet(wb, buildKeyStatsSheet(keyStats), 'Key Stats');
  XLSX.utils.book_append_sheet(wb, buildPortfolioTotalSheet(portfolioTotal), 'Portfolio Total');
  XLSX.utils.book_append_sheet(wb, buildSACSheet(sacRows, fundsBySac), 'Brinson by SAC');
  XLSX.utils.book_append_sheet(wb, buildContributorsSheet(contributions), 'Top Bottom Contributors');

  const today = new Date().toISOString().slice(0, 10);
  const suffix = isRange
    ? `RANGE_${rangeStart}_${rangeEnd}`
    : reportType === 'SI' ? 'SI' : `${reportType}_${periodEnd || 'latest'}`;
  const filename = sanitiseFilename(`Attribution_${portfolioCode}_${suffix}_${today}.xlsx`);
  XLSX.writeFile(wb, filename);
  return filename;
};
